import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  LogOut,
  Menu,
  X,
  Home,
  Crown,
  Activity,
  ArrowUpFromLine,
  User,
  Gift,
  Bell,
  CreditCard,
  Wallet,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { navItems } from "../data/mockData";

const bottomNavItems = [
  { label: "Home", path: "/", icon: Home },
  { label: "VIP", path: "/vip", icon: Crown },
  { label: "Activity", path: "/transactions", icon: Activity },
  { label: "Withdraw", path: "/withdraw", icon: ArrowUpFromLine },
  { label: "Profile", path: "/settings", icon: User },
];

export default function Layout({ children, title = "Dashboard" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(function () {
    if (
      location.pathname === "/request-points" ||
      location.pathname === "/withdraw"
    ) {
      setPaymentOpen(true);
    }
  }, [location.pathname]);

  const getUser = function () {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) {
        return null;
      }

      return JSON.parse(storedUser);
    } catch (error) {
      console.error("USER PARSE ERROR:", error);
      return null;
    }
  };

  const user = getUser();

  const handleLogout = function () {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  const closeMobile = function () {
    setMobileOpen(false);
  };

  const navClass = function (props) {
    const isActive = props.isActive;

    return (
      "group relative flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-medium " +
      "transition-all duration-300 " +
      (isActive
        ? "bg-gradient-to-r from-[#5B8DEF]/20 via-[#5B8DEF]/10 to-transparent text-[#AFC6FF] shadow-[inset_0_0_20px_rgba(91,141,239,0.05)]"
        : "text-[#8C97B2] hover:bg-white/[0.045] hover:text-[#F4F6FB]")
    );
  };

  const Sidebar = function () {
    return (
      <aside
        className="
          relative flex h-full w-[264px] flex-col overflow-hidden
          border-r border-white/[0.07]
          bg-[#090D17]/95
          backdrop-blur-2xl
        "
      >
        {/* Ambient sidebar glow */}
        <div className="pointer-events-none absolute -left-20 -top-20 h-52 w-52 rounded-full bg-[#5B8DEF]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-[#9067E8]/10 blur-3xl" />

        {/* Logo */}
        <div className="relative flex h-[78px] shrink-0 items-center border-b border-white/[0.07] px-5">
          <button
            type="button"
            onClick={function () {
              navigate("/");
            }}
            className="group flex items-center gap-3"
          >
            <div
              className="
                relative flex h-11 w-11 items-center justify-center
                overflow-hidden rounded-2xl
                bg-gradient-to-br from-[#F2C15B] via-[#C99435] to-[#9067E8]
                shadow-[0_8px_30px_rgba(144,103,232,0.28)]
                transition-all duration-300
                group-hover:scale-105
                group-hover:shadow-[0_10px_35px_rgba(144,103,232,0.42)]
              "
            >
              <div className="absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <Crown
                size={20}
                strokeWidth={2.3}
                className="relative z-10 text-[#080C15]"
              />
            </div>

            <div className="text-left">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-extrabold tracking-[0.08em] text-[#F4F6FB]">
                  VIP EARN
                </p>

                <Sparkles
                  size={12}
                  className="text-[#E0A63C]"
                  strokeWidth={2.2}
                />
              </div>

              <p className="mt-1 text-[9px] font-medium tracking-[0.2em] text-[#59647D]">
                EARN · GROW · SUCCEED
              </p>
            </div>
          </button>
        </div>

        {/* Navigation */}
        <div className="relative flex-1 overflow-y-auto px-3 py-5 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10">
          <div className="mb-3 flex items-center justify-between px-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4F5A73]">
              Main Menu
            </p>

            <div className="h-1 w-1 rounded-full bg-[#5B8DEF]" />
          </div>

          <nav className="space-y-1">
            {navItems.map(function (item) {
              const Icon = item.icon;

              if (item.children) {
                const paymentActive =
                  location.pathname === "/request-points" ||
                  location.pathname === "/withdraw";

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      onClick={function () {
                        setPaymentOpen(function (prev) {
                          return !prev;
                        });
                      }}
                      className={
                        "group relative flex w-full items-center justify-between rounded-2xl px-3.5 py-3 text-sm font-medium transition-all duration-300 " +
                        (paymentActive || paymentOpen
                          ? "bg-white/[0.05] text-[#F4F6FB]"
                          : "text-[#8C97B2] hover:bg-white/[0.045] hover:text-[#F4F6FB]")
                      }
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={
                            "flex h-9 w-9 items-center justify-center rounded-xl border transition-all duration-300 " +
                            (paymentActive || paymentOpen
                              ? "border-[#5B8DEF]/20 bg-gradient-to-br from-[#5B8DEF]/20 to-[#5B8DEF]/5 text-[#7EA8FF] shadow-[0_0_18px_rgba(91,141,239,0.12)]"
                              : "border-white/[0.04] bg-white/[0.035] text-[#66728D] group-hover:border-white/[0.08] group-hover:text-[#9AA8C7]")
                          }
                        >
                          <Icon size={17} strokeWidth={2} />
                        </span>

                        <span>Payment Method</span>
                      </span>

                      <ChevronDown
                        size={16}
                        strokeWidth={2}
                        className={
                          "transition-all duration-300 " +
                          (paymentOpen
                            ? "rotate-180 text-[#7EA8FF]"
                            : "text-[#59647D] group-hover:text-[#8C97B2]")
                        }
                      />
                    </button>

                    {paymentOpen ? (
                      <div className="relative ml-5 mt-1.5 space-y-1 border-l border-white/[0.08] pl-3">
                        <NavLink
                          to="/request-points"
                          onClick={closeMobile}
                          className={function (props) {
                            const isActive = props.isActive;

                            return (
                              "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium transition-all duration-300 " +
                              (isActive
                                ? "bg-[#5B8DEF]/10 text-[#8EB1FF]"
                                : "text-[#737F99] hover:bg-white/[0.04] hover:text-[#D7DCE8]")
                            );
                          }}
                        >
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#5B8DEF]/10 text-[#5B8DEF] transition-transform duration-300 group-hover:scale-105">
                            <CreditCard size={14} strokeWidth={2} />
                          </span>

                          <span>Add Payment</span>

                          <ChevronRight
                            size={13}
                            className="ml-auto opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-60"
                          />
                        </NavLink>

                        <NavLink
                          to="/withdraw"
                          onClick={closeMobile}
                          className={function (props) {
                            const isActive = props.isActive;

                            return (
                              "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium transition-all duration-300 " +
                              (isActive
                                ? "bg-[#E5566B]/10 text-[#F07889]"
                                : "text-[#737F99] hover:bg-white/[0.04] hover:text-[#D7DCE8]")
                            );
                          }}
                        >
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#E5566B]/10 text-[#E5566B] transition-transform duration-300 group-hover:scale-105">
                            <ArrowUpFromLine size={14} strokeWidth={2} />
                          </span>

                          <span>Withdraw</span>

                          <ChevronRight
                            size={13}
                            className="ml-auto opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-60"
                          />
                        </NavLink>
                      </div>
                    ) : null}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  onClick={closeMobile}
                  className={navClass}
                >
                  {function (props) {
                    const isActive = props.isActive;

                    return (
                      <React.Fragment>
                        {isActive ? (
                          <>
                            <span className="absolute left-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-[#7EA8FF] to-[#5B8DEF] shadow-[0_0_12px_rgba(91,141,239,0.65)]" />

                            <span className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-[#5B8DEF]/5 to-transparent" />
                          </>
                        ) : null}

                        <span
                          className={
                            "relative z-10 flex h-9 w-9 items-center justify-center rounded-xl border transition-all duration-300 " +
                            (isActive
                              ? "border-[#5B8DEF]/20 bg-gradient-to-br from-[#5B8DEF]/20 to-[#5B8DEF]/5 text-[#7EA8FF] shadow-[0_0_18px_rgba(91,141,239,0.1)]"
                              : "border-white/[0.04] bg-white/[0.035] text-[#66728D] group-hover:border-white/[0.08] group-hover:bg-white/[0.055] group-hover:text-[#9AA8C7]")
                          }
                        >
                          <Icon size={17} strokeWidth={2} />
                        </span>

                        <span className="relative z-10">{item.label}</span>
                      </React.Fragment>
                    );
                  }}
                </NavLink>
              );
            })}
          </nav>

          {/* Referral Card */}
          <div
            className="
              group relative mt-7 overflow-hidden rounded-2xl
              border border-white/[0.07]
              bg-gradient-to-br from-[#30265C] via-[#211A42] to-[#151329]
              p-4
              shadow-[0_15px_40px_rgba(0,0,0,0.18)]
            "
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#9067E8]/20 blur-2xl transition-all duration-500 group-hover:bg-[#9067E8]/30" />

            <div className="relative z-10">
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E0A63C]/10">
                  <Gift size={15} className="text-[#E0A63C]" />
                </span>

                <span className="text-sm font-bold text-white">
                  Invite Friends
                </span>
              </div>

              <p className="mb-3 text-xs leading-5 text-[#B9C1D9]">
                Earn more rewards for every referral.
              </p>

              <NavLink
                to="/referrals"
                onClick={closeMobile}
                className="
                  group/button flex w-full items-center justify-center gap-2
                  rounded-xl border border-white/[0.08]
                  bg-gradient-to-r from-[#7063D7] to-[#866FE9]
                  py-2.5 text-sm font-semibold text-white
                  shadow-[0_8px_20px_rgba(123,109,232,0.22)]
                  transition-all duration-300
                  hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(123,109,232,0.32)]
                "
              >
                Invite Now
                <ChevronRight
                  size={14}
                  className="transition-transform duration-300 group-hover/button:translate-x-0.5"
                />
              </NavLink>
            </div>
          </div>
        </div>

        {/* User Section */}
        <div className="relative shrink-0 border-t border-white/[0.07] p-3">
          <div
            className="
              mb-2 flex items-center gap-3 rounded-2xl
              border border-white/[0.05]
              bg-white/[0.025]
              p-3
              transition-all duration-300
              hover:border-white/[0.08]
              hover:bg-white/[0.04]
            "
          >
            <div className="relative">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#5B8DEF] via-[#746CE3] to-[#9067E8] text-sm font-bold text-white shadow-[0_5px_18px_rgba(91,141,239,0.2)]">
                {user && user.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>

              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-[#0A0E19] bg-[#20D69B]" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-[#F4F6FB]">
                {(user && user.name) || "User"}
              </p>

              <p className="mt-0.5 truncate text-[10px] text-[#59647D]">
                {(user && user.email) || "Account"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="
              group flex w-full items-center gap-3 rounded-xl
              px-3.5 py-3 text-sm font-medium
              text-[#7C879F]
              transition-all duration-300
              hover:bg-[#E5566B]/10
              hover:text-[#F07889]
            "
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-transparent transition-all duration-300 group-hover:bg-[#E5566B]/10">
              <LogOut size={17} />
            </span>

            Logout
          </button>
        </div>
      </aside>
    );
  };

  const isBottomNavActive = function (item) {
    return location.pathname === item.path;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#080C15] text-[#F4F6FB]">
      {/* Global ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -left-40 -top-40 h-[420px] w-[420px] rounded-full bg-[#5B8DEF]/[0.035] blur-[120px]" />
        <div className="absolute -bottom-40 right-0 h-[420px] w-[420px] rounded-full bg-[#9067E8]/[0.035] blur-[120px]" />
      </div>

      {/* Desktop Sidebar */}
      <div className="fixed inset-y-0 left-0 z-40 hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile Overlay */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/65 backdrop-blur-sm lg:hidden"
          onClick={closeMobile}
        />
      ) : null}

      {/* Mobile Sidebar */}
      <div
        className={
          "fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-out lg:hidden " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full")
        }
      >
        <Sidebar />
      </div>

      <div className="relative z-10 lg:pl-[264px]">
        {/* Mobile Header */}
        <header
          className="
            sticky top-0 z-30 flex items-center justify-between
            border-b border-white/[0.07]
            bg-[#080C15]/90
            px-4 backdrop-blur-2xl
            lg:hidden
          "
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
            paddingBottom: "12px",
          }}
        >
          <button
            type="button"
            onClick={function () {
              setMobileOpen(function (prev) {
                return !prev;
              });
            }}
            className="
              flex h-11 w-11 items-center justify-center
              rounded-xl border border-white/[0.07]
              bg-white/[0.035]
              text-[#F4F6FB]
              transition-all duration-300
              hover:bg-white/[0.07]
              active:scale-95
            "
          >
            {mobileOpen ? <X size={21} /> : <Menu size={21} />}
          </button>

          <div className="flex flex-col items-center">
            <h1
              className="text-base font-semibold tracking-wide text-[#F4F6FB]"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {title}
            </h1>

            <div className="mt-1 flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-[#5B8DEF]" />
              <span className="text-[8px] font-medium uppercase tracking-[0.2em] text-[#59647D]">
                VIP EARN
              </span>
            </div>
          </div>

          <Link
            to="/support"
            className="
              relative flex h-11 w-11 items-center justify-center
              rounded-xl border border-white/[0.07]
              bg-white/[0.035]
              text-[#F4F6FB]
              transition-all duration-300
              hover:bg-white/[0.07]
              active:scale-95
            "
          >
            <Bell size={19} />

            <span className="absolute right-2.5 top-2 h-1.5 w-1.5 rounded-full bg-[#5B8DEF] shadow-[0_0_7px_rgba(91,141,239,0.8)]" />
          </Link>
        </header>

        {/* Main Content */}
        <main className="min-h-[calc(100vh-64px)] p-4 pb-28 sm:p-6 lg:min-h-screen lg:p-8 lg:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav
        className="
          fixed bottom-3 left-3 right-3 z-40
          rounded-3xl
          border border-white/[0.09]
          bg-[#0E1220]/90
          shadow-[0_15px_50px_rgba(0,0,0,0.45)]
          backdrop-blur-2xl
          lg:hidden
        "
        style={{
          paddingTop: "8px",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <div className="flex items-stretch justify-between">
          {bottomNavItems.map(function (item) {
            const Icon = item.icon;
            const active = isBottomNavActive(item);

            return (
              <NavLink
                key={item.label}
                to={item.path}
                className="
                  group relative flex flex-1 flex-col
                  items-center justify-center gap-1
                  rounded-2xl py-2
                "
                style={{
                  minWidth: 0,
                }}
              >
                {/* Active background */}
                <div
                  className={
                    "absolute inset-x-1 top-1 bottom-1 rounded-2xl transition-all duration-300 " +
                    (active
                      ? "bg-[#5B8DEF]/10 opacity-100"
                      : "opacity-0 group-hover:bg-white/[0.035] group-hover:opacity-100")
                  }
                />

                <div
                  className={
                    "relative z-10 flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-300 " +
                    (active
                      ? "bg-[#5B8DEF]/15 text-[#6F9BFF]"
                      : "text-[#5D6880] group-hover:text-[#8C97B2]")
                  }
                >
                  <Icon
                    size={19}
                    strokeWidth={active ? 2.3 : 2}
                    style={{
                      filter: active
                        ? "drop-shadow(0 0 7px rgba(91,141,239,0.65))"
                        : "none",
                    }}
                  />
                </div>

                <span
                  className={
                    "relative z-10 text-[10px] font-semibold leading-none transition-colors duration-300 " +
                    (active
                      ? "text-[#6F9BFF]"
                      : "text-[#5D6880] group-hover:text-[#8C97B2]")
                  }
                >
                  {item.label}
                </span>

                {active ? (
                  <span className="absolute bottom-0.5 h-0.5 w-5 rounded-full bg-[#5B8DEF] shadow-[0_0_8px_rgba(91,141,239,0.7)]" />
                ) : null}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function CreditCardIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );
}

function WithdrawIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}